import type { BankAccountType } from '@api';
import { ActionsCell, BankAccountStatusBadge } from '@common';
import { DataTableColumn } from 'mantine-datatable';
import { useTranslation } from 'react-i18next';

type UseBankAccountsTableColumnsProps = {
  onView: (bankAccount: BankAccountType) => void;
  onEdit: (bankAccount: BankAccountType) => void;
  onApprove: (bankAccountId: string) => void;
  onDelete: (bankAccountId: string) => void;
  canEdit?: boolean;
};

export type UseBankAccountsTableColumnsReturnType = {
  columns: DataTableColumn<BankAccountType>[];
};

export const useBankAccountsTableColumns = ({
  onView,
  onEdit,
  onApprove,
  onDelete,
  canEdit = false,
}: UseBankAccountsTableColumnsProps): UseBankAccountsTableColumnsReturnType => {
  const { t } = useTranslation();
  const noDataPlaceholder = t('NO_DATA_PLACEHOLDER');

  const columns: DataTableColumn<BankAccountType>[] = [
    {
      accessor: 'bankName',
      title: t('BANK_ACCOUNTS.TABLE.BANK'),
      render: (record) => record.bankName || noDataPlaceholder,
    },
    {
      accessor: 'accountHolderName',
      title: t('BANK_ACCOUNTS.TABLE.ACCOUNT_HOLDER'),
      render: (record) => record.accountHolderName || noDataPlaceholder,
    },
    {
      accessor: 'iban',
      title: t('BANK_ACCOUNTS.TABLE.IBAN'),
      render: (record) => record.iban || noDataPlaceholder,
    },
    {
      accessor: 'status',
      title: t('BANK_ACCOUNTS.TABLE.STATUS'),
      render: (record) => <BankAccountStatusBadge status={record.status} />,
    },
    {
      accessor: 'actions',
      title: t('BANK_ACCOUNTS.TABLE.ACTIONS'),
      titleStyle: { textAlign: 'center' },
      render: (record) => (
        <ActionsCell.Root>
          {record.canBeApproved && (
            <ActionsCell.Approve onClick={() => onApprove(record.id)} />
          )}
          {record.status === 'DELETED' && (
            <ActionsCell.View onClick={() => onView(record)} />
          )}
          {record.status !== 'DELETED' && (
            <>
              <ActionsCell.Edit onClick={() => onEdit(record)} />
              <ActionsCell.Delete onClick={() => onDelete(record.id)} />
            </>
          )}
        </ActionsCell.Root>
      ),
    },
  ];

  const allowedColumns = columns.filter((column) => {
    if (column.accessor === 'actions') {
      return canEdit;
    }
    return true;
  });

  return { columns: allowedColumns };
};
